import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { setTimeoutInDev } from '@/utils/environment'
import apiFetch, { createApiAsyncThunk } from '@/services/apiService'
import { addFlashMessage } from '../flash/flashMessagesSlice'
import { sanitizeFormData } from '@/utils/formData'

const URL = 'contacts'

export const CivilitesMapping = {
  "Monsieur": "M.",
  "Madame": "Mme",
  "Mademoiselle": "Mlle"
}

const sanitizeContactFormData = (data) => {
  const formData = sanitizeFormData(data)

  // Traiter les contacts
  if (formData.contacts && Array.isArray(formData.contacts)) {
    formData.relation_physiques = formData.contacts
      .filter(item => item.id !== undefined)
      .map(item => ({
        contact_physique_id: item.id
      }))
  }
  delete formData.contacts
  delete formData.nom_complet
  delete formData.roles
  return formData
}

export const contactFilters = {
  personne_physiques: {label: "Personnes physiques", value: 'personne_physiques', key: 'type'},
  personne_morales: {label: "Sociétés", value: 'personne_morales', key: 'type'},
  proprietaires: { label: "Propriétaires / bailleurs", value: 'proprietaires', key: 'nature'},
  acquereurs: {label: "Acquéreurs", value: 'acquereurs', key: 'nature'},
  locataires: {label: "Locataires", value: 'locataires', key: 'nature'},
}

export const contactFiltersIds = [
  "personne_physiques", "personne_morales",
  "proprietaires", "acquereurs", "locataires"
]
// Retourne dans un ordre prédéterminé les filtres disponibles
// avec la valeur "checked" à true s'ils sont séléctionnés
// dans le state
// exemple :
// state est ainsi : currentFilters = { type: "personne_physiques" }
// retournera {
//    { label: "Personnes physiques", value: "personne_physiques", key: "type", checked: true },
//    { ... , checked: false },
//    ...
// } 
export const ordonnedContactFilters = (currentFilters) => contactFiltersIds.map(
  key => ({
    ...contactFilters[key], 
    checked: currentFilters[contactFilters[key].key] === contactFilters[key].value
  })
)

// Thunks
export const fetchContacts = createAsyncThunk(
  'contacts/fetchContacts',
  async (params = {}, { dispatch, getState, rejectWithValue }) => {
    try {
      // Construire l'URL avec les paramètres
      let url = URL
      const response = await apiFetch()
        .url(url)
        .query(params)
        .get()
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const fetchContact = createAsyncThunk(
  'biens/fetchContact',
  async (id, { dispatch, getState, rejectWithValue }) => {
    await setTimeoutInDev()
    try {
      const response = await apiFetch()
        .url(`${URL}/${id}`)
        .get()
      return response
    } catch (error) {
      return rejectWithValue(error.message || 'Une erreur est survenue lors de la récupération du contact')
    }
  }
)

export const createContact = createApiAsyncThunk(
  'contacts/createContact',
  async (contactData, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(URL)
        .post({contact: sanitizeContactFormData(contactData)})
      dispatch(addFlashMessage({text: "Contact créé"}))
      return response
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
);

export const updateContact = createApiAsyncThunk(
  'contacts/updateContact',
  async ({ id, contactData }, {api, dispatch, rejectWithValue }) => {
    try {
      await setTimeoutInDev()
      const response = await api
        .url(`${URL}/${id}`)
        .put({contact: sanitizeContactFormData(contactData)})
      dispatch(setCurrentContact(response))
      dispatch(addFlashMessage({text: "Contact mis à jour"}))
      return response
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

export const archiveContact = createApiAsyncThunk(
  'contacts/archiveContact',
  async (id, { api, dispatch, rejectWithValue }) => {
    await setTimeoutInDev()
    try {
      const response = await api
        .url(`${URL}/${id}/archive`)
        .put()
      dispatch(setCurrentContact(response))
      dispatch(addFlashMessage({type: 'success', text: 'Contact archivé'}))
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const restoreContact = createApiAsyncThunk(
  'contacts/restoreContact',
  async (id, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(`${URL}/${id}/restore`)
        .put()
      dispatch(setCurrentContact(response))
      dispatch(addFlashMessage({type: 'success', text: 'Contact restoré'}))
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const deleteContact = createApiAsyncThunk(
  'biens/deleteContact',
  async (id, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(`${URL}/${id}`)
        .delete()

      dispatch(addFlashMessage({type: 'success', text: "Le contact a été supprimé avec succès"}))
      return response
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

export const emptyContact = {
    type: 'PersonnePhysique',
    civilite: 'Monsieur',
    nom: '',
    prenom: '',
    date_naissance: '',
    lieu_naissance: '',
    adresse_ligne1: '',
    adresse_ligne2: '',
    adresse_ligne3: '',
    code_postal: '',
    ville: '',
    country: '',
    telephone: '',
    email: '',
    profession: '',
    gli: false,
    garants: '',
    numero_piece_identite: '',
    siret: '',
  }

// Initial state
const initialState = {
  items: [],
  current: null,
  // Remplacer loading par un objet qui contient des états de chargement distincts
  loadingStatus: {
    fetchList: false,
    create: false,
    update: false,
    delete: false,
    archive: false,
    restore: false,
  },
  requestStatus: {
    success: false,
    error: null,
    errors: {},
  },
  filters: {
    page: 1,
    limit: 100
  },
  pagination: {
    totalCount: 0,
    totalPages: 0,
    currentPage: 1
  }
}

const sanitizesPayload = (action) => Object.fromEntries(
  Object.entries(action.payload).map(([key, value]) => 
    [key, value === null ? (emptyContact[key] || "") : value]
  )
);

// Slice
const contactsSlice = createSlice({
  name: 'contacts',
  initialState,
  reducers: {
    setEmptyCurrentContact: (state, action) => {
      const type = action.payload && action.payload.type || emptyContact.type
      state.current = {
        ...emptyContact,
        type
      }
      // Réinitialiser le statut de la requête
      state.requestStatus = initialState.requestStatus
    },
    setCurrentContact: (state, action) => {
      state.current = {
        ...emptyContact,
        ...sanitizesPayload(action)
      }
      // Réinitialiser le statut de la requête
      state.requestStatus = initialState.requestStatus
    },
    resetCurrentContact: (state) => {
      state.current = initialState.current
      // Réinitialiser le statut de la requête
      state.requestStatus = initialState.requestStatus
    },
    setFilters: (state, {payload}) => {
      state.filters = { ...state.filters, ...payload }
    },
    // Ajout d'un reducer pour mettre à jour un champ spécifique du contact (utilisé pour les champs dynamiques
    updateContactField: (state, action) => {
      state.current[action.payload.field] = action.payload.value;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch contacts
      .addCase(fetchContacts.pending, (state) => {
        state.loadingStatus.fetchList = true
        state.requestStatus.error = null
      })
      .addCase(fetchContacts.fulfilled, (state, action) => {
        state.items = action.payload.contacts
        state.pagination = {
          totalCount: action.payload.total_count,
          totalPages: action.payload.total_pages,
          currentPage: action.payload.current_page
        }
        state.loadingStatus.fetchList = false
        state.requestStatus.success = true
      })
      .addCase(fetchContacts.rejected, (state, action) => {
        state.loadingStatus.fetchList = false
        state.requestStatus.error = action.payload
      })
      // Fetch contact
      .addCase(fetchContact.pending, (state) => {
        state.loadingStatus.fetchItem = true
        state.requestStatus.error = null
      })
      .addCase(fetchContact.fulfilled, (state, action) => {
        state.loadingStatus.fetchItem = false
        state.current = action.payload
        state.requestStatus.success = true
      })
      .addCase(fetchContact.rejected, (state, action) => {
        state.loadingStatus.fetchItem = false
        state.requestStatus.error = action.payload
      })
      
      // Add contact
      .addCase(createContact.pending, (state) => {
        state.loadingStatus.create = true
        state.requestStatus.error = null
        state.requestStatus.errors = {}
        state.requestStatus.success = false
      })
      .addCase(createContact.fulfilled, (state, action) => {
        state.loadingStatus.create = false
        state.items.unshift(action.payload)
        state.requestStatus.error = null
        state.requestStatus.errors = {}
        state.requestStatus.success = true
      })
      .addCase(createContact.rejected, (state, action) => {        
        state.loadingStatus.create = false
        state.requestStatus.errors = action.payload.errors ? action.payload.errors[0] : {} // Store validation errors
        state.requestStatus.error = action.payload
        state.requestStatus.success = false
      })
      
      // Update contact
      .addCase(updateContact.pending, (state) => {
        state.loadingStatus.update = true
        state.requestStatus.error = null
        state.requestStatus.errors = {}
        state.requestStatus.success = false
      })
      .addCase(updateContact.fulfilled, (state, action) => {
        state.loadingStatus.update = false
        const index = state.items.findIndex(contact => contact.id === action.payload.id)
        if (index !== -1) {
          state.items[index] = action.payload
        }
        state.requestStatus.error = null
        state.requestStatus.errors = {}
        state.requestStatus.success = true
      })
      .addCase(updateContact.rejected, (state, action) => {
        state.loadingStatus.update = false
        state.requestStatus.error = action.payload
        state.requestStatus.errors = action.payload.errors ? action.payload.errors[0] : {} // Store validation errors
        state.requestStatus.success = false
      })
       
      // Archive contact
      .addCase(archiveContact.pending, (state) => {
        state.loadingStatus.archive = true
        state.requestStatus.error = null
      })
      .addCase(archiveContact.fulfilled, (state, action) => {
        state.loadingStatus.archive = false
        const index = state.items.findIndex(contact => contact.id === action.payload.id)
        if (index !== -1) {
          state.items[index] = action.payload
        }
        state.requestStatus.success = true
      })
      .addCase(archiveContact.rejected, (state, action) => {
        state.loadingStatus.archive = false
        state.requestStatus.error = action.payload
      })
      
      // Restore contact
      .addCase(restoreContact.pending, (state) => {
        state.loadingStatus.restore = true
        state.requestStatus.error = null
      })
      .addCase(restoreContact.fulfilled, (state, action) => {
        state.loadingStatus.restore = false
        const index = state.items.findIndex(contact => contact.id === action.payload.id)
        if (index !== -1) {
          state.items[index] = action.payload
        }
        state.requestStatus.success = true
      })
      .addCase(restoreContact.rejected, (state, action) => {
        state.loadingStatus.restore = false
        state.requestStatus.error = action.payload
      })
      .addCase(deleteContact.pending, (state) => {
        state.loadingStatus.delete = true
        state.requestStatus.error = null
      })
      .addCase(deleteContact.fulfilled, (state, action) => {
        state.loadingStatus.delete = false
        const index = state.items.findIndex( item => item.id === action.payload.id )
        if (index !== -1) {
          state.items.splice(index, 1)
        }
        state.requestStatus.success = true
      })
      .addCase(deleteContact.rejected, (state, action) => {
        state.loadingStatus.delete = false
        state.requestStatus.success = false
        state.requestStatus.error = action.payload
      })
  }
})

export const { 
  setCurrentContact, setEmptyCurrentContact, updateContactField,
  setFilters,
} = contactsSlice.actions

export default contactsSlice.reducer